// src/components/support/forms/WithdrawChild.js
import React, { useEffect, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useAtom } from "jotai";

import { useRequestProcessor } from "../../../requestProcessor";
import { handleWithdrawChild } from "../../../queryfunctions/queryfunctions";
import { activeChildDetail, appConstants, childList } from "../../../atomstore/store";
import SurveyForm from "../../common/SurveyForm";
import FootprintDatePicker, { DEFAULT_FORMAT } from "../../common/FootprintDatePicker";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../common/CustomSelect";
import SpinnerLoading from "../../common/SpinnerLoading";



const WithdrawChild = () => {
    const navigate = useNavigate();
    const [activeChild] = useAtom(activeChildDetail);
    const [constants] = useAtom(appConstants);
    const { useMutationWrapper } = useRequestProcessor();
    const [children] = useAtom(childList)
    const [selectedChild, setSelectedChild] = useState(0)
    const [state, setState] = useState({
        withdrawDate: undefined,
        reason: undefined,
        otherRemark: undefined,
        rating: undefined,
        tags: undefined,
        comment: undefined,
        error: undefined,
    });

    console.log("state", state)
    useEffect(() => {
        console.log("[WithdrawChild] Initialized with child:", activeChild?.id);
    }, [activeChild?.id]);

    const withdrawChild = useMutationWrapper(
        ["withdrawChild", "withdraw" + activeChild?.id],
        () => handleWithdrawChild(state, children[selectedChild]),
        {
            onSuccess: (res) => {
                console.log("[WithdrawChild] Success:", res);
                // onBack()
            },
        }
    );

    const validate = () => {
        if (!state.withdrawDate) {
            setState({ ...state, error: "Please select withdrawal date" });
            return false;
        }
        if (!state.reason) {
            setState({ ...state, error: "Please select reason" });
            return false;
        }
        if (state.reason === "Others" && !state.otherRemark) {
            setState({ ...state, error: "Please enter reason" });
            return false;
        }
        if (!state.rating) {
            setState({ ...state, error: "Please select rating" });
            return false;
        }
        if (!state.tags || state.tags.length === 0) {
            setState({ ...state, error: "Please select tags" });
            return false;
        }
        if (!state.comment) {
            setState({ ...state, error: "Please enter comment" });
            return false;
        }
        return true;
    };

    const submit = () => {
        if (validate()) {
            withdrawChild.mutate();
        }
    };

    const selectedChildName = children[selectedChild]?.name;
    const firstName = selectedChildName?.split(" ")[0];

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {withdrawChild.isLoading && <SpinnerLoading />}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Withdraw Child</h2>
            </div>

            <div className="p-4 space-y-5">
                {/* Child Selection */}
                {children.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>
                        <CustomSelect
                            value={selectedChild}
                            onChange={(val) => setSelectedChild(Number(val))}
                            options={children.map((child, idx) => ({
                                value: idx,
                                label: `${child.name} ${child?.last_name}`,
                            }))}
                            placeholder="Select Child"
                        />
                    </div>
                )}

                {/* Info box */}
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
                    <p className="text-sm text-amber-800">
                        We're sorry to see {firstName} go. Please provide details to complete the withdrawal process.
                    </p>
                </div>

                {/* Withdraw Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Day at School
                    </label>
                    <FootprintDatePicker
                        holidaySelected={(selected) =>
                            setState({
                                ...state,
                                error: "The selected Date Cannot Be a Holiday",
                                holidaySelected: selected,
                            })
                        }
                        inputProps={{ placeholder: "Select Date" }}
                        minimumDate={moment().startOf("day")}
                        onChange={(date) =>
                            setState({
                                ...state,
                                holidaySelected: false,
                                withdrawDate: moment(date).format(DEFAULT_FORMAT),
                                error: undefined,
                            })
                        }
                    />
                </div>

                {/* Reason */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason for Withdrawal
                    </label>
                    <CustomSelect
                        value={state.reason || ""}
                        onChange={(val) =>
                            setState({
                                ...state,
                                reason: val,
                                otherRemark: undefined,
                                error: undefined,
                            })
                        }
                        options={[
                            { value: "", label: "Select reason" },
                            ...(constants?.WithdrawReason?.map((item, idx) => ({
                                value: item,
                                label: item,
                            })) || []),
                        ]}
                        placeholder="Select reason"
                    />
                </div>

                {/* Others Textbox */}
                {state.reason === "Others" && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Please specify
                        </label>
                        <textarea
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 outline-none focus:border-transparent"
                            placeholder="Enter your reason"
                            onChange={(e) =>
                                setState({
                                    ...state,
                                    otherRemark: e.target.value,
                                    error: undefined,
                                })
                            }
                        />
                    </div>
                )}

                {/* Survey */}
                <SurveyForm
                    key={children[selectedChild]?.id}
                    onRatingPressed={(rating) =>
                        setState({ ...state, rating, error: undefined })
                    }
                    getTags={(tags) =>
                        setState({ ...state, tags, error: undefined })
                    }
                    getComment={(comment) =>
                        setState({ ...state, comment, error: undefined })
                    }
                    selectedChild={children[selectedChild]}
                />

                {/* Error */}
                {state.error && (
                    <p className="text-red-600 text-sm font-medium">
                        {state.error}
                    </p>
                )}

                {/* Submit */}
                <button
                    onClick={submit}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition"
                >
                    Submit Withdrawal Request
                </button>
            </div>
        </div>
    );
};

export default WithdrawChild;
